/**
 * main-search.js
 * M&S tarzı arama overlay için güncellenmiş versiyon.
 * Default state (popüler kategoriler + featured products) ve
 * Results state (yazarken predictive search sonuçları) yönetir.
 */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // ── Element referansları ──────────────────────────────────────────
    this._input        = this.querySelector('.ms-form__input, .main-search__input');
    this._clearBtn     = this.querySelector('.ms-form__clear');
    this._defaultState = this.querySelector('#ms-default-state');
    this._resultsBox   = this.querySelector('#ms-results-state, .ms-results, .main-search__results');

    // ── Kapat / Geri butonları (main-search__close class'lılar) ───────
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        this._closeOverlay();
      });
    });

    // ── Escape tuşu ──────────────────────────────────────────────────
    if (this._input) {
      this._input.addEventListener('keyup', (evt) => {
        if (evt.key === 'Escape') this._closeOverlay();
      });

      // Temizle butonu görünürlüğü
      this._input.addEventListener('input', () => this._syncClearBtn());
    }

    // ── Temizle butonu ───────────────────────────────────────────────
    if (this._clearBtn) {
      this._clearBtn.addEventListener('click', () => {
        if (this._input) {
          this._input.value = '';
          this._input.focus();
        }
        this._syncClearBtn();
        this._showDefault();
      });
    }

    // ── Overlay açıldığında input'a fokuslan ─────────────────────────
    this._watchBodyClass();

    // ── Quick search (yazarken arama) ─────────────────────────────────
    if (this.dataset.quickSearch === 'true') {
      this._initQuickSearch();
    }

    // ── Ürün kartı slider (custom-slide dot/swipe) global init ────────
    this._initProductSliders();
  }

  // ── Overlay'i kapat ────────────────────────────────────────────────
  _closeOverlay() {
    document.body.classList.remove('show-search');
    // Header geri yükleme işlemi _watchBodyClass observer'ında yapılır
    // (overlay başka yollarla kapansa da çalışsın diye)
  }

  // ── Temizle butonu sync ────────────────────────────────────────────
  _syncClearBtn() {
    if (!this._clearBtn || !this._input) return;
    this._clearBtn.classList.toggle('ms-show', this._input.value.length > 0);
  }

  // ── Default state göster ──────────────────────────────────────────
  _showDefault() {
    if (this._defaultState) this._defaultState.classList.remove('ms-hidden');
    if (this._resultsBox)   this._resultsBox.classList.remove('ms-show');
    this.classList.remove('ms-loading', 'ms-has-results', 'ms-no-results');
  }

  // ── Results state göster ──────────────────────────────────────────
  _showResults() {
    if (this._defaultState) this._defaultState.classList.add('ms-hidden');
    if (this._resultsBox)   this._resultsBox.classList.add('ms-show');
  }

  // ── Ürün kartı slider (dot tıklama + swipe) ───────────────────────
  // snippets/product-block.liquid içindeki script ile aynı mantık.
  // O script normalde sadece bir product-block render edildiğinde
  // sayfaya yüklenir; bazı sayfalarda (örn. ana sayfa) hiç
  // yüklenmediği için arama sonuçlarındaki dot/swipe çalışmaz.
  // window.__nmxProductSliderInit guard'ı ile tekrar kayıt önlenir.
  _initProductSliders() {
    if (window.__nmxProductSliderInit) return;
    window.__nmxProductSliderInit = true;

    const updateSlider = (container, index) => {
      if (!container) return;
      const targetIndex = String(index);
      container.querySelectorAll('.custom-slide').forEach((slide) => {
        const active = slide.getAttribute('data-index') === targetIndex;
        slide.classList.toggle('is-active', active);
        slide.classList.toggle('is-hidden', !active);
      });
      container.querySelectorAll('.js-nav-dot').forEach((dot) => {
        dot.classList.toggle('active', dot.getAttribute('data-index') === targetIndex);
      });
    };

    // Dot tıklama
    document.addEventListener('click', (evt) => {
      const dot = evt.target.closest('.js-nav-dot');
      if (dot) {
        evt.preventDefault();
        evt.stopPropagation();
        updateSlider(dot.closest('.js-slider-parent'), dot.getAttribute('data-index'));
      }
    }, true);

    // Swipe (dokunmatik)
    let startX = 0;
    let startY = 0;

    document.addEventListener('touchstart', (evt) => {
      const area = evt.target.closest('.js-swipe-area');
      if (area) {
        startX = evt.touches[0].clientX;
        startY = evt.touches[0].clientY;
      }
    }, { passive: true });

    document.addEventListener('touchend', (evt) => {
      const area = evt.target.closest('.js-swipe-area');
      if (!area) return;

      const deltaX = startX - evt.changedTouches[0].clientX;
      const deltaY = startY - evt.changedTouches[0].clientY;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 30) {
        const container = area.closest('.js-slider-parent');
        if (!container) return;
        const dots = container.querySelectorAll('.js-nav-dot');
        const activeDot = container.querySelector('.js-nav-dot.active');
        if (activeDot && dots.length) {
          const current = parseInt(activeDot.getAttribute('data-index'), 10);
          const next = deltaX > 0
            ? (current + 1) % dots.length
            : (current - 1 + dots.length) % dots.length;
          updateSlider(container, next);
        }
      }
    }, { passive: true });
  }

  // ── Body class değişikliğini izle (overlay açıldığında fokuslan) ──
  _watchBodyClass() {
    const observer = new MutationObserver(() => {
      const pageHeader = document.getElementById('pageheader');
      const isOpen = document.body.classList.contains('show-search');

      if (isOpen) {

        // Desktop'ta overlay top'unu header'ın o anki gerçek bottom'una göre ayarla
        // (scroll edilince duyuru bandı kayar, 97px sabit değer boşluk yaratır)
        if (window.innerWidth >= 769) {
          const headerSection = document.querySelector('.section-header');
          if (headerSection) {
            const top = Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom));
            this.style.top      = top + 'px';
            this.style.maxHeight = `calc(100vh - ${top}px)`;
          }
        } else {
          this.style.top      = '';
          this.style.maxHeight = '';
        }

        // Header saydamsa, opak (normal scrolled) görünüme geçir
        if (pageHeader && pageHeader.classList.contains('pageheader--transparent')) {
          pageHeader.classList.remove('pageheader--transparent');
          this._restoreTransparentHeader = true;
        }

        // Kısa gecikme ile fokuslan (CSS animasyonu tamamlansın)
        setTimeout(() => {
          if (this._input) this._input.focus();
          this._syncClearBtn();
          // Input dolu değilse default state'i göster
          if (!this._input || !this._input.value.length) {
            this._showDefault();
          }
        }, 80);

      } else if (this._restoreTransparentHeader) {
        // Overlay kapandı (hangi yoldan olursa olsun) — header saydamlığını geri ver
        this._restoreTransparentHeader = false;
        if (pageHeader && window.scrollY <= 50) {
          pageHeader.classList.add('pageheader--transparent');
        }
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Quick search: yazarken predictive arama ───────────────────────
  _initQuickSearch() {
    const searchInput     = this._input;
    const throttleMs      = 500;
    const resultLimit     = 8;
    const includeMeta     = this.dataset.quickSearchMeta === 'true';
    let   timeoutID       = -1;
    let   abortController = null;

    const handleChange = () => {
      const query = searchInput.value;

      // ── Değer var ve değişti ────────────────────────────────────────
      if (query.length && query !== this._oldQuery) {
        this._oldQuery = query;

        // Önceki isteği iptal et
        if (abortController) {
          abortController.abort('Superseded');
          abortController = null;
        }
        clearTimeout(timeoutID);

        // View-all URL'i hazırla
        const form    = searchInput.closest('form');
        const linkURL = new URL(form.action);
        new URLSearchParams(new FormData(form)).forEach((v, k) => linkURL.searchParams.set(k, v));

        // Loading göster
        this._showResults();
        this.classList.remove('ms-has-results', 'ms-no-results');
        this.classList.add('ms-loading');
        this._resultsBox.innerHTML = '<div class="ms-spinner-wrap"><div class="ms-spinner"></div></div>';

        // Arama isteği (throttled)
        timeoutID = setTimeout(() => {
          abortController = new AbortController();
          let ajaxURL;

          if (theme.Shopify.features.predictiveSearch) {
            ajaxURL = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxURL.searchParams.set('q', query);
            ajaxURL.searchParams.set('section_id', 'predictive-search');
            ajaxURL.searchParams.set('resources[limit]', resultLimit);
            ajaxURL.searchParams.set(
              'resources[options][fields]',
              includeMeta
                ? 'title,product_type,variants.title,vendor,tag,variants.sku'
                : 'title,product_type,variants.title,vendor'
            );
          } else {
            ajaxURL = new URL(linkURL);
            ajaxURL.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxURL, { method: 'get', signal: abortController.signal })
            .then((res) => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.text();
            })
            .then((html) => {
              const tpl = document.createElement('template');
              tpl.innerHTML = html;

              const resultsList = theme.Shopify.features.predictiveSearch
                ? tpl.content.querySelector('.product-grid')
                : tpl.content.querySelector('.section-search-template .product-grid');

              abortController = null;
              this.classList.remove('ms-loading');
              this._resultsBox.innerHTML = '';

              // ── Ürünler ─────────────────────────────────────────────
              const productsWrap = document.createElement('div');
              productsWrap.className = 'ms-results__products main-search__results__products collection-listing';
              productsWrap.innerHTML = '<div></div>';
              productsWrap.firstElementChild.className = resultsList.className;

              resultsList
                .querySelectorAll('.product-block:not(.collection-block):not(.page-block)')
                .forEach((block, i) => {
                  if (i <= resultLimit) {
                    block.classList.add('main-search-result');
                    block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                    block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                    productsWrap.firstElementChild.appendChild(block);
                  }
                });

              // ── Sayfalar ─────────────────────────────────────────────
              const pagesWrap = document.createElement('div');
              pagesWrap.className = 'ms-results__pages';

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const a = document.createElement('a');
                a.className = 'main-search-result main-search-result--page';
                a.href = block.querySelector('a').href;
                a.innerHTML = '<div class="main-search-result__text"></div>';
                a.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                pagesWrap.appendChild(a);
              });

              const hasProducts = !!productsWrap.querySelector('.main-search-result');
              const hasPages    = !!pagesWrap.querySelector('.main-search-result');

              if (hasProducts || hasPages) {
                this.classList.add('ms-has-results');
                this.classList.toggle(
                  'main-search--results-on-multiple-lines',
                  productsWrap.querySelectorAll('.product-block').length > 4
                );

                if (hasProducts) this._resultsBox.appendChild(productsWrap);

                if (hasPages) {
                  const h = document.createElement('h6');
                  h.className = 'ms-results__pages-label';
                  h.innerHTML = theme.strings.generalSearchPages;
                  pagesWrap.insertAdjacentElement('afterbegin', h);
                  this._resultsBox.appendChild(pagesWrap);
                }

                // "Tümünü Gör" linki
                const allLink = document.createElement('a');
                allLink.className = 'ms-results__view-all btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                this._resultsBox.appendChild(allLink);

              } else {
                // Sonuç yok
                this.classList.add('ms-no-results');
                const empty = document.createElement('div');
                empty.className = 'ms-results__empty main-search__empty-message';
                empty.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                this._resultsBox.appendChild(empty);
              }
            })
            .catch((err) => {
              if (err.name === 'AbortError') return;
              console.warn('[ms-search]', err); // eslint-disable-line no-console
              this.classList.remove('ms-loading');
            });

        }, throttleMs);

      // ── Input temizlendi ────────────────────────────────────────────
      } else if (!query.length) {
        this._oldQuery = '';
        if (abortController) {
          abortController.abort('Cleared');
          abortController = null;
        }
        clearTimeout(timeoutID);
        if (this._resultsBox) this._resultsBox.innerHTML = '';
        this._showDefault();
      }
    };

    searchInput.addEventListener('keyup',  handleChange.bind(this));
    searchInput.addEventListener('change', handleChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);